import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { SILENT_ERRORS } from '../interceptors/error.interceptor';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  ApiCollectionResponse,
  ApiSingleResponse,
} from '../interfaces/responses/api-response';
import {
  AccessCatalog,
  AccessProfile,
  AccountPermissions,
  AccountProfiles,
  CatalogSync,
  SaveAccessProfile,
  StaffAccount,
} from '../interfaces/access';
import { InspectionScope, SaveInspectionScope } from '../interfaces/inspection-scope';

/**
 * Profils d'accès et leur attribution — réservé au superviseur.
 *
 * GET    /api/admin/access/catalog
 * POST   /api/admin/access/catalog/sync
 * GET    /api/admin/access/profiles
 * POST   /api/admin/access/profiles
 * PUT    /api/admin/access/profiles/{key}
 * DELETE /api/admin/access/profiles/{key}
 * PUT    /api/admin/access/base-roles/{key}
 * GET    /api/admin/access/accounts
 * PUT    /api/admin/access/accounts/{userId}/profiles
 * PUT    /api/admin/access/accounts/{userId}/permissions
 * GET    /api/admin/access/accounts/{userId}/inspection-scope
 * PUT    /api/admin/access/accounts/{userId}/inspection-scope
 */
@Injectable({ providedIn: 'root' })
export class AccessService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.BASE_URL}/admin/access`;

  /**
   * Les lectures échouent surtout faute de droits Keycloak, et le serveur le
   * dit en clair (« accordez view-clients… »). L'intercepteur remplacerait ce
   * message par un « erreur interne » générique : l'écran l'affiche lui-même.
   * Les trois écritures ajoutées avec les accès fins font de même.
   */
  private readonly silent = { context: new HttpContext().set(SILENT_ERRORS, true) };

  catalog(): Observable<ApiSingleResponse<AccessCatalog>> {
    return this.http.get<ApiSingleResponse<AccessCatalog>>(`${this.base}/catalog`, this.silent);
  }

  /**
   * Crée dans Keycloak les permissions du catalogue qui y manquent, et donne
   * les héritières d'une permission scindée aux rôles qui la portaient. Se
   * rejoue sans effet. Silencieuse : un refus de Keycloak (503) nomme le droit
   * qui manque au compte de service, et c'est ce message que l'écran affiche.
   */
  syncCatalog(): Observable<ApiSingleResponse<CatalogSync>> {
    return this.http.post<ApiSingleResponse<CatalogSync>>(`${this.base}/catalog/sync`, null, this.silent);
  }

  profiles(): Observable<ApiCollectionResponse<AccessProfile>> {
    return this.http.get<ApiCollectionResponse<AccessProfile>>(`${this.base}/profiles`, this.silent);
  }

  createProfile(dto: SaveAccessProfile): Observable<ApiSingleResponse<AccessProfile>> {
    return this.http.post<ApiSingleResponse<AccessProfile>>(`${this.base}/profiles`, dto);
  }

  updateProfile(key: string, dto: SaveAccessProfile): Observable<ApiSingleResponse<AccessProfile>> {
    return this.http.put<ApiSingleResponse<AccessProfile>>(
      `${this.base}/profiles/${encodeURIComponent(key)}`, dto);
  }

  /**
   * Règle les permissions d'un rôle de base — `admin`, le seul que le serveur
   * laisse régler. La liste <b>remplace</b> le jeu : vide, elle retire tout.
   * Ni le nom ni l'existence du rôle ne se décident ici.
   */
  updateBaseRole(key: string, permissions: string[]): Observable<ApiSingleResponse<AccessProfile>> {
    return this.http.put<ApiSingleResponse<AccessProfile>>(
      `${this.base}/base-roles/${encodeURIComponent(key)}`, { permissions }, this.silent);
  }

  deleteProfile(key: string): Observable<ApiSingleResponse<string>> {
    return this.http.delete<ApiSingleResponse<string>>(
      `${this.base}/profiles/${encodeURIComponent(key)}`);
  }

  accounts(): Observable<ApiCollectionResponse<StaffAccount>> {
    return this.http.get<ApiCollectionResponse<StaffAccount>>(`${this.base}/accounts`, this.silent);
  }

  setAccountProfiles(userId: string, profiles: string[]): Observable<ApiSingleResponse<AccountProfiles>> {
    return this.http.put<ApiSingleResponse<AccountProfiles>>(
      `${this.base}/accounts/${userId}/profiles`, { profiles });
  }

  /**
   * Les permissions <b>individuelles</b> d'un compte : celles qu'on ne donne
   * qu'à lui. La liste remplace le jeu, et lui seul — ce que le compte tient
   * de son rôle de base ou de ses profils n'en fait pas partie.
   */
  setAccountPermissions(userId: string, permissions: string[]): Observable<ApiSingleResponse<AccountPermissions>> {
    return this.http.put<ApiSingleResponse<AccountPermissions>>(
      `${this.base}/accounts/${userId}/permissions`, { permissions }, this.silent);
  }

  /** Le périmètre d'inspection d'un compte — attribut du compte, pas du profil. */
  inspectionScope(userId: string): Observable<ApiSingleResponse<InspectionScope>> {
    return this.http.get<ApiSingleResponse<InspectionScope>>(
      `${this.base}/accounts/${userId}/inspection-scope`, this.silent);
  }

  setInspectionScope(userId: string, dto: SaveInspectionScope): Observable<ApiSingleResponse<InspectionScope>> {
    return this.http.put<ApiSingleResponse<InspectionScope>>(
      `${this.base}/accounts/${userId}/inspection-scope`, dto);
  }
}
